import { useState, useEffect, useRef } from 'react'
import { MagnifyingGlass, X, Spinner } from '@phosphor-icons/react'
import { Input } from './ui/input'
import { Label } from './ui/label'

interface NCMItem {
  codigo: string
  descricao: string
}

interface NCMAutocompleteProps {
  value?: string
  onChange?: (value: string) => void
  onSelect?: (codigo: string, descricao?: string) => void
  label?: string
  placeholder?: string
  error?: string
  required?: boolean
  className?: string
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:4000'

export function NCMAutocomplete({
  value = '',
  onChange,
  onSelect,
  label = 'NCM',
  placeholder = 'Digite o código ou descrição do NCM',
  error,
  required = false,
  className = ''
}: NCMAutocompleteProps) {
  const [inputValue, setInputValue] = useState(value)
  const [suggestions, setSuggestions] = useState<NCMItem[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedItem, setSelectedItem] = useState<NCMItem | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Sync external value
  useEffect(() => {
    if (value !== inputValue && !isOpen) {
      setInputValue(value)
    }
  }, [value])

  const searchNCM = async (query: string) => {
    if (query.length < 2) {
      setSuggestions([])
      return
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController()

    setIsLoading(true)
    try {
      const response = await fetch(
        `${API_BASE}/ncm/search?q=${encodeURIComponent(query)}&limit=15`,
        { signal: abortControllerRef.current.signal }
      )
      const data = await response.json()
      setSuggestions(data.results || [])
    } catch (error: any) {
      // Ignore abort errors
      if (error.name !== 'AbortError') {
        console.error('NCM search error:', error)
        setSuggestions([])
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)
    setSelectedItem(null)
    onChange?.(newValue)
    setIsOpen(true)

    // Debounce search (500ms to reduce API calls)
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(() => {
      searchNCM(newValue)
    }, 500)
  }

  const handleSelect = (item: NCMItem) => {
    setInputValue(item.codigo)
    setSelectedItem(item)
    onChange?.(item.codigo)
    onSelect?.(item.codigo, item.descricao)
    setIsOpen(false)
    setSuggestions([])
  }

  const handleClear = () => {
    setInputValue('')
    setSelectedItem(null)
    onChange?.('')
    setSuggestions([])
  }

  const handleFocus = () => {
    if (inputValue.length >= 2 && suggestions.length === 0) {
      searchNCM(inputValue)
    }
    setIsOpen(true)
  }

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      {label && (
        <Label className="mb-1.5 block">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      )}

      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          {isLoading ? (
            <Spinner size={18} className="animate-spin" />
          ) : (
            <MagnifyingGlass size={18} />
          )}
        </div>

        <Input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          className="pl-10 pr-10"
        />

        {inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Selected item description */}
      {selectedItem && (
        <p className="text-xs text-emerald-600 mt-1 truncate" title={selectedItem.descricao}>
          {selectedItem.descricao}
        </p>
      )}

      {/* Error message */}
      {error && (
        <p className="text-xs text-red-500 mt-1">{error}</p>
      )}

      {/* Dropdown suggestions */}
      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((item, index) => (
            <button
              key={item.codigo}
              type="button"
              onClick={() => handleSelect(item)}
              className={`w-full px-4 py-3 text-left hover:bg-emerald-50 transition-colors border-b border-gray-100 last:border-b-0
                ${index === 0 ? 'rounded-t-lg' : ''}
                ${index === suggestions.length - 1 ? 'rounded-b-lg' : ''}`}
            >
              <div className="flex items-start gap-3">
                <span className="font-mono text-sm font-semibold text-emerald-600 whitespace-nowrap">
                  {item.codigo}
                </span>
                <span className="text-sm text-gray-700 line-clamp-2">
                  {item.descricao}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* No results message */}
      {isOpen && inputValue.length >= 2 && !isLoading && suggestions.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-sm text-gray-500">
          Nenhum NCM encontrado para "{inputValue}"
        </div>
      )}

      {/* Helper text */}
      {!selectedItem && !error && (
        <p className="text-xs text-gray-500 mt-1">
          Pesquise por código (ex: 8471) ou descrição (ex: computador)
        </p>
      )}
    </div>
  )
}
